import { isV2Plan, type PaywallState } from '@highlight/common'
import { useUnifiedBilling } from '@renderer/chat/store/global-billing'

/** Tooltip copy for every brief control this gate disables. */
export const MEETING_HOURS_EXHAUSTED_TOOLTIP =
  "You're out of meeting hours. Top up or upgrade your plan to prepare briefs."

/**
 * Whether the meeting-hours balance is spent, which is what the backend's
 * brief gate refuses new generation runs on.
 *
 * Mirrors that gate's scope rather than the paywall meter's. Only the v2 paid
 * tiers are enforced, so a legacy or free plan reads as not exhausted even
 * though its meter shows 0 of 0 hours. The client must never be stricter than
 * the backend, or a plan the backend still generates for sits behind a
 * disabled button with no way to ask for the brief it would happily produce.
 * Loading reads the same way, since `defaultPaywallState` starts at
 * `remaining: 0` and disabling on that grays the button out for a beat on
 * every launch.
 */
export function isMeetingHoursExhausted(paywall: PaywallState): boolean {
  if (paywall.isLoading || !isV2Plan(paywall.plan)) {
    return false
  }
  // null total = unlimited allowance; nothing to exhaust.
  return paywall.meetingHours.total !== null && (paywall.meetingHours.remaining ?? 0) <= 0
}

/** {@link isMeetingHoursExhausted} against the live billing state. */
export function useMeetingHoursExhausted(): boolean {
  return isMeetingHoursExhausted(useUnifiedBilling().paywall)
}
